import { AxiosResponse } from "axios";
import {
  ComunicadoCampanhaStatus,
  ComunicadoCreatePayload,
  ComunicadoPreviewPayload,
  IComunicadoCampanha,
  IComunicadoEnviosResponse,
  IComunicadoListResponse,
  IComunicadoPreview,
  IContagemPublico,
  IFiltrosPublicoEvento,
  IVariavelComunicado,
  ComunicadoCanal,
  ComunicadoPublico,
} from "interfaces/comunicado";
import api from "./api";

function ensureSuccess<T>(response: AxiosResponse<T>): T {
  if (response.status >= 400) {
    const data = response.data as { message?: string };
    throw new Error(data?.message || "Falha na comunicação com o servidor");
  }
  return response.data;
}

function normalizeError(error: unknown) {
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      "Falha na comunicação com o servidor",
  );
}

export async function listarComunicados(params?: {
  page?: number;
  limit?: number;
  status?: ComunicadoCampanhaStatus;
}): Promise<IComunicadoListResponse> {
  try {
    const response = await api.get<IComunicadoListResponse>("/comunicados", {
      params,
    });
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterComunicado(id: string): Promise<IComunicadoCampanha> {
  try {
    const response = await api.get<IComunicadoCampanha>(`/comunicados/${id}`);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function listarEnviosComunicado(
  id: string,
  params?: {
    page?: number;
    limit?: number;
    status?: string;
    canal?: string;
  },
): Promise<IComunicadoEnviosResponse> {
  try {
    const response = await api.get<IComunicadoEnviosResponse>(
      `/comunicados/${id}/envios`,
      { params },
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterVariaveisComunicado(): Promise<IVariavelComunicado[]> {
  try {
    const response = await api.get<{ variaveis: IVariavelComunicado[] }>(
      "/comunicados/variaveis",
    );
    const data = ensureSuccess(response);
    return data.variaveis ?? [];
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function contarPublicoComunicado(params: {
  publico: ComunicadoPublico;
  canal: ComunicadoCanal;
  eventoId?: number;
  userId?: string;
  filtroCidade?: string;
  filtroEquipe?: string;
}): Promise<IContagemPublico> {
  try {
    const response = await api.get<IContagemPublico>(
      "/comunicados/publico/contagem",
      { params },
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterFiltrosPublicoEvento(): Promise<IFiltrosPublicoEvento> {
  try {
    const response = await api.get<IFiltrosPublicoEvento>("/comunicados/publico/filtros");
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function previewComunicado(
  payload: ComunicadoPreviewPayload,
): Promise<IComunicadoPreview> {
  try {
    const response = await api.post<IComunicadoPreview>(
      "/comunicados/preview",
      payload,
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function criarComunicado(
  payload: ComunicadoCreatePayload,
): Promise<{ id: string; status: string; totalDestinatarios: number; message: string }> {
  try {
    const response = await api.post("/comunicados", payload);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function cancelarComunicado(
  id: string,
): Promise<{ id: string; status: string; message: string }> {
  try {
    const response = await api.post(`/comunicados/${id}/cancelar`);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}
